/**
 * Practice-mode card for the einbuergerungstest app.
 *
 * Shows the German question + 4 shuffled multiple-choice options. Auxiliary
 * affordances:
 *   - Translation toggle (per-card, not sticky): reveals question_en + EN of
 *     each option below the German text.
 *   - Tip icon: always visible in the header; accent-colored when the card
 *     has a tip, gray when it doesn't. Opens an inline panel.
 *   - Bookmark icon: toggles the persistent bookmark via PATCH /cards/:id.
 *
 * Submitting an option calls `onAnswer` with the correctness boolean and the
 * letter the user chose. The PracticeShell handles the feedback animation +
 * advances to the next card.
 *
 * The CardFront receives `Card<EinbuergerungsPayload>` rather than the
 * NounsPayload that nouns/derdiedas use. The PracticeRoute shell casts
 * generically and each AppDefinition.CardFront re-narrows its prop type.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Bookmark, Info } from 'lucide-react';

import type { EinbuergerungsPayload, OptionLetter } from '@/core/types';
import { api } from '@/lib/api';
import { seededShuffle } from '@/lib/shuffle';
import { cn } from '@/lib/utils';

import type { CardFrontProps } from '../types';
import { QuestionImage } from './QuestionImage';

/**
 * Optional bookmark control. When provided, CardFront becomes controlled —
 * the parent owns the bookmarked state and the click handler. Used by the
 * SimulationRoute to defer bookmark persistence until submit so an abandoned
 * sim doesn't leak bookmarks into the persistent state.
 */
export type BookmarkOverride = {
  bookmarked: boolean;
  onToggle: () => void;
};

type Props = CardFrontProps<EinbuergerungsPayload> & {
  bookmarkOverride?: BookmarkOverride;
};

export function CardFront({ card, onAnswer, bookmarkOverride }: Props) {
  const startRef = useRef<number>(Date.now());
  const [showTranslation, setShowTranslation] = useState(false);
  const [showTip, setShowTip] = useState(false);

  // Uncontrolled bookmark state (used only when bookmarkOverride is absent).
  const [uncontrolledBookmarked, setUncontrolledBookmarked] = useState<boolean>(
    card.bookmarked ?? false,
  );
  const [bookmarkPending, setBookmarkPending] = useState(false);

  const isControlled = !!bookmarkOverride;
  const bookmarked = isControlled ? bookmarkOverride!.bookmarked : uncontrolledBookmarked;

  // Reset per-card state when the card changes.
  useEffect(() => {
    startRef.current = Date.now();
    setShowTranslation(false);
    setShowTip(false);
    if (!isControlled) {
      setUncontrolledBookmarked(card.bookmarked ?? false);
    }
  }, [card.id, card.bookmarked, isControlled]);

  // Shuffle the four options per render of a fresh card so position can't be
  // memorised. The shuffle is stable across re-renders of the same card.
  const shuffledOptions = useMemo(
    () => seededShuffle(card.payload.options, card.id),
    [card.id, card.payload.options],
  );

  const submit = (chosen: OptionLetter | null) => {
    const correct = chosen === card.payload.correct_letter;
    onAnswer({
      correct,
      user_answer: chosen,
      time_ms: Date.now() - startRef.current,
    });
  };

  const toggleBookmark = async () => {
    if (isControlled) {
      bookmarkOverride!.onToggle();
      return;
    }
    if (bookmarkPending) return;
    const next = !bookmarked;
    setUncontrolledBookmarked(next);
    setBookmarkPending(true);
    try {
      await api.toggleBookmark(card.id, next);
    } catch {
      // Roll back on failure; surface no toast — the user will see the icon
      // revert. A future iteration could log this via the existing toast lib.
      setUncontrolledBookmarked(!next);
    } finally {
      setBookmarkPending(false);
    }
  };

  const hasTip = !!card.payload.tip_en;

  return (
    <div className="border-border bg-card relative flex w-full flex-col gap-6 rounded-2xl border p-6 shadow-sm sm:p-8">
      {/* Top-right affordances: tip + bookmark. */}
      <div className="absolute top-4 right-4 flex items-center gap-1.5">
        <CardIconButton
          aria-label={hasTip ? 'Show tip' : 'No tip for this question'}
          onClick={() => hasTip && setShowTip((v) => !v)}
          active={showTip}
          dim={!hasTip}
        >
          <Info className="h-5 w-5" />
        </CardIconButton>
        <CardIconButton
          aria-label={bookmarked ? 'Remove bookmark' : 'Bookmark this question'}
          onClick={toggleBookmark}
          active={bookmarked}
        >
          <Bookmark className={cn('h-5 w-5', bookmarked && 'fill-current')} />
        </CardIconButton>
      </div>

      {/* Question text. */}
      <header className="pr-20">
        {card.external_id ? (
          <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
            Question {card.external_id}
            {card.payload.scope === 'state' && card.payload.state_code
              ? ` · ${card.payload.state_code}`
              : ''}
          </p>
        ) : null}
        <h2 className="font-display mt-2 text-xl leading-snug font-semibold sm:text-2xl">
          {card.de}
        </h2>
        {showTranslation ? (
          <p className="text-muted-foreground mt-2 text-base italic sm:text-lg">
            {card.payload.question_en}
          </p>
        ) : (
          <button
            type="button"
            onClick={() => setShowTranslation(true)}
            className="text-primary mt-2 text-sm font-medium underline-offset-4 hover:underline"
          >
            Show translation
          </button>
        )}
      </header>

      {/* Optional tip panel. */}
      {showTip && hasTip ? (
        <div className="border-primary/40 bg-primary/5 text-foreground rounded-lg border p-4 text-sm">
          {card.payload.tip_en}
        </div>
      ) : null}

      {card.payload.has_image && card.external_id ? (
        <QuestionImage bamfId={card.external_id} size="md" />
      ) : null}

      {/* Four options, vertically stacked (options are full sentences). */}
      <div className="flex flex-col gap-2.5">
        {shuffledOptions.map((opt) => (
          <OptionButton
            key={opt.letter}
            letter={opt.letter}
            de={opt.de}
            en={showTranslation ? opt.en : null}
            onClick={() => submit(opt.letter)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={() => submit(null)}
        className="text-muted-foreground mx-auto text-sm underline-offset-4 hover:underline"
      >
        I don't know
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

function OptionButton({
  letter,
  de,
  en,
  onClick,
}: {
  letter: OptionLetter;
  de: string;
  en: string | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="border-border bg-card hover:border-primary hover:bg-primary/5 focus-visible:ring-ring flex w-full items-start gap-3 rounded-lg border-2 p-4 text-left transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
    >
      <span className="bg-muted mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm font-semibold">
        {letter}
      </span>
      <span className="flex-1 leading-snug">
        <span className="block text-base">{de}</span>
        {en ? <span className="text-muted-foreground mt-1 block text-sm italic">{en}</span> : null}
      </span>
    </button>
  );
}

function CardIconButton({
  children,
  onClick,
  active,
  dim,
  ...rest
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  dim?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-muted-foreground flex h-9 w-9 items-center justify-center rounded-md transition-colors',
        'hover:bg-muted focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
        active && 'text-primary',
        dim && 'opacity-40',
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

// Deterministic shuffle (stable per card, varying card-to-card) now lives in
// @/lib/shuffle — the vokabeln app needs the same behaviour plus seeded
// sampling of its distractor pool.
